package com.example.histoire.web

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.ClientRequestException
import io.ktor.client.plugins.ResponseException
import io.ktor.client.plugins.ServerResponseException
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.encodeToJsonElement
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import kotlinx.serialization.json.put
import kotlin.js.Promise

/**
 * Même valeur que Supabase > Authentication > Email > « Email OTP length ».
 * Mettre à jour ici si tu modifies ce réglage dans le dashboard.
 */
const val SUPABASE_WEB_EMAIL_OTP_DIGITS: Int = 8

private const val SESSION_STORAGE_KEY = "histoire_du_soir_supabase_session_v1"
private const val PKCE_VERIFIER_KEY = "histoire_supabase_pkce_verifier_v1"
private const val RECOVERY_PENDING_KEY = "histoire_supabase_recovery_pending_v1"
private const val RECOVERY_UI_LOCK_KEY = "histoire_supabase_recovery_ui_lock_v1"
private const val PASSKEY_ENABLED_HINT_KEY = "histoire_passkey_enabled_hint_v1"
private const val BIOMETRIC_SESSION_STASH_KEY = "histoire_biometric_session_stash_v1"
private const val BIOMETRIC_REENABLED_NOTICE_KEY = "histoire_biometric_reenabled_notice_v1"
private const val OPEN_PARENT_PROFILE_ONCE_KEY = "histoire_open_parent_profile_once_v1"
private const val LAST_LOGIN_EMAIL_KEY = "histoire_last_login_email_v1"
private const val PASSKEY_REQUIRES_PASSWORD_LOGIN_KEY = "histoire_passkey_requires_password_login_v1"
private const val APP_UNLOCK_SESSION_KEY = "histoire_app_unlock_done_v1"

private val jsonFormat = Json {
    ignoreUnknownKeys = true
    isLenient = true
}

@Serializable
private data class StoredSupabaseSession(
    val accessToken: String,
    val refreshToken: String,
    val expiresAt: Long?,
    val userId: String,
)

@Serializable
data class PasskeySessionPayload(
    @SerialName("access_token") val accessToken: String,
    @SerialName("refresh_token") val refreshToken: String,
    @SerialName("user_id") val userId: String,
    @SerialName("expires_at") val expiresAt: Long? = null,
    val email: String? = null,
)

@Serializable
private data class ChildProfileRow(
    val id: String,
    @SerialName("first_name") val firstName: String,
    @SerialName("birth_date") val birthDate: String,
)

@Serializable
private data class UserProfileFetchRow(
    @SerialName("user_id") val userId: String? = null,
    @SerialName("first_name") val firstName: String? = null,
    @SerialName("last_name") val lastName: String? = null,
    @SerialName("contact_email") val contactEmail: String? = null,
    @SerialName("display_name") val displayName: String? = null,
)

@Serializable
private data class ChildProfileInsert(
    val id: String,
    @SerialName("user_id") val userId: String,
    @SerialName("first_name") val firstName: String,
    @SerialName("birth_date") val birthDate: String,
    @SerialName("sort_order") val sortOrder: Int,
)

private fun loadPersistedSession(): StoredSupabaseSession? {
    return try {
        val raw = localStorage.getItem(SESSION_STORAGE_KEY).orEmpty().trim()
        if (raw.isNotBlank()) {
            return jsonFormat.decodeFromString(StoredSupabaseSession.serializer(), raw)
        }
        // Fallback "stash biométrique permanent" : utile si la session active est perdue,
        // mais qu'une session biométrique valide existe encore localement.
        val stash = localStorage.getItem(BIOMETRIC_SESSION_STASH_KEY).orEmpty().trim()
        if (stash.isBlank()) return null
        jsonFormat.decodeFromString(StoredSupabaseSession.serializer(), stash)
    } catch (_: Throwable) {
        null
    }
}

private fun savePersistedSession(session: StoredSupabaseSession?) {
    if (session == null) {
        localStorage.removeItem(SESSION_STORAGE_KEY)
        // Sinon loadPersistedSession() retombe sur le stash biométrique et recrée une « fausse » session.
        localStorage.removeItem(BIOMETRIC_SESSION_STASH_KEY)
        return
    }
    val encoded = jsonFormat.encodeToString(StoredSupabaseSession.serializer(), session)
    localStorage.setItem(SESSION_STORAGE_KEY, encoded)
    // Stash biométrique permanent: gardé en miroir quand la biométrie est active.
    if (isPasskeyHintEnabledLocally()) {
        localStorage.setItem(BIOMETRIC_SESSION_STASH_KEY, encoded)
    }
}

fun clearSupabaseWebSession() {
    savePersistedSession(null)
    localStorage.removeItem(PKCE_VERIFIER_KEY)
    runCatching { window.sessionStorage.removeItem(APP_UNLOCK_SESSION_KEY) }
}

fun isPasskeyBlockedUntilPasswordLogin(): Boolean =
    localStorage.getItem(PASSKEY_REQUIRES_PASSWORD_LOGIN_KEY)?.trim() == "1"

fun isPasskeyHintEnabledLocally(): Boolean =
    localStorage.getItem(PASSKEY_ENABLED_HINT_KEY)?.trim() == "1"

fun setPasskeyHintEnabledLocally(enabled: Boolean) {
    if (enabled) localStorage.setItem(PASSKEY_ENABLED_HINT_KEY, "1")
    else {
        localStorage.removeItem(PASSKEY_ENABLED_HINT_KEY)
        localStorage.removeItem(BIOMETRIC_SESSION_STASH_KEY)
    }
}

fun markBiometricReenabledNoticePending() {
    localStorage.setItem(BIOMETRIC_REENABLED_NOTICE_KEY, "1")
}

fun consumeBiometricReenabledNoticePending(): Boolean {
    val pending = localStorage.getItem(BIOMETRIC_REENABLED_NOTICE_KEY)?.trim() == "1"
    localStorage.removeItem(BIOMETRIC_REENABLED_NOTICE_KEY)
    return pending
}

fun markOpenParentProfileOncePending() {
    localStorage.setItem(OPEN_PARENT_PROFILE_ONCE_KEY, "1")
}

fun consumeOpenParentProfileOncePending(): Boolean {
    val pending = localStorage.getItem(OPEN_PARENT_PROFILE_ONCE_KEY)?.trim() == "1"
    localStorage.removeItem(OPEN_PARENT_PROFILE_ONCE_KEY)
    return pending
}

fun hasInTabAppUnlock(): Boolean =
    runCatching { window.sessionStorage.getItem(APP_UNLOCK_SESSION_KEY)?.trim() == "1" }.getOrDefault(false)

fun markInTabAppUnlockDone() {
    runCatching { window.sessionStorage.setItem(APP_UNLOCK_SESSION_KEY, "1") }
}

fun clearInTabAppUnlock() {
    runCatching { window.sessionStorage.removeItem(APP_UNLOCK_SESSION_KEY) }
}

fun isPasskeyEnrollmentSupported(): Boolean =
    runCatching {
        js(
            """(function(){
              return !!(window.isSecureContext &&
                window.PublicKeyCredential &&
                navigator.credentials &&
                navigator.credentials.create);
            })""",
        ).unsafeCast<Boolean>()
    }.getOrDefault(false)

fun saveSupabaseSessionFromPasskey(payload: PasskeySessionPayload) {
    savePersistedSession(
        StoredSupabaseSession(
            accessToken = payload.accessToken,
            refreshToken = payload.refreshToken,
            expiresAt = payload.expiresAt ?: jwtExpEpochSeconds(payload.accessToken),
            userId = payload.userId,
        ),
    )
}

private fun urlDecodeComponent(s: String): String =
    try {
        js("decodeURIComponent")(s.replace("+", " ")) as String
    } catch (_: Throwable) {
        s
    }

private fun parseQueryToMap(query: String): Map<String, String> {
    if (query.isBlank()) return emptyMap()
    val out = mutableMapOf<String, String>()
    for (part in query.split("&")) {
        if (part.isBlank()) continue
        val idx = part.indexOf('=')
        val rawKey = if (idx < 0) part else part.substring(0, idx)
        val rawVal = if (idx < 0) "" else part.substring(idx + 1)
        out[urlDecodeComponent(rawKey)] = urlDecodeComponent(rawVal)
    }
    return out
}

/**
 * Extrait la valeur à envoyer à GoTrue /verify et le mode :
 * - `token_hash` : POST avec seulement type + token_hash (pas d'e-mail — exigence GoTrue).
 * - `null` : code OTP saisi : POST avec type + token + email.
 *
 * Important : sur les liens Supabase « réinitialisation », le paramètre d'URL `token`
 * est traité comme un token_hash côté serveur, pas comme un OTP à combiner avec l'e-mail.
 */
private fun extractRecoveryInput(rawInput: String): Pair<String, String?> {
    val trimmed = rawInput.trim()
    if (trimmed.isBlank()) return "" to null
    val lower = trimmed.lowercase()
    val looksLikeAuthUrl =
        "://" in trimmed ||
            lower.startsWith("http://") ||
            lower.startsWith("https://") ||
            "supabase.co/auth/" in lower ||
            "token_hash=" in lower ||
            "type=recovery" in lower ||
            ("token=" in lower && "recovery" in lower)
    if (looksLikeAuthUrl) {
        val queryPart = trimmed.substringAfter('?', "").substringBefore('#')
        val hashPart = trimmed.substringAfter('#', "")
        val query = parseQueryToMap(queryPart)
        val hash = parseQueryToMap(hashPart)
        val tokenHash = query["token_hash"]?.trim().orEmpty().ifEmpty { hash["token_hash"]?.trim().orEmpty() }
        val token = query["token"]?.trim().orEmpty().ifEmpty { hash["token"]?.trim().orEmpty() }
        return when {
            tokenHash.isNotEmpty() -> tokenHash to "token_hash"
            token.isNotEmpty() -> token to "token_hash"
            else -> trimmed.replace(Regex("\\s+"), "") to null
        }
    }
    return trimmed.replace(Regex("\\s+"), "") to null
}

private fun decodeJwtSub(jwt: String): String? {
    return try {
        val parts = jwt.split(".")
        if (parts.size < 2) return null
        var payload = parts[1].replace('-', '+').replace('_', '/')
        when (payload.length % 4) {
            2 -> payload += "=="
            3 -> payload += "="
        }
        val decoded = js("atob")(payload) as String
        val m = Regex("\"sub\"\\s*:\\s*\"([^\"]+)\"").find(decoded) ?: return null
        m.groupValues[1]
    } catch (_: Throwable) {
        null
    }
}

private fun decodeJwtEmail(jwt: String): String? {
    return try {
        val parts = jwt.split(".")
        if (parts.size < 2) return null
        var payload = parts[1].replace('-', '+').replace('_', '/')
        when (payload.length % 4) {
            2 -> payload += "=="
            3 -> payload += "="
        }
        val decoded = js("atob")(payload) as String
        val m = Regex("\"email\"\\s*:\\s*\"([^\"]+)\"").find(decoded) ?: return null
        m.groupValues[1]
    } catch (_: Throwable) {
        null
    }
}

private fun jwtExpEpochSeconds(token: String): Long? {
    return try {
        val parts = token.split(".")
        if (parts.size < 2) return null
        var payload = parts[1].replace('-', '+').replace('_', '/')
        when (payload.length % 4) {
            2 -> payload += "=="
            3 -> payload += "="
        }
        val decoded = js("atob")(payload) as String
        val expMatch = Regex("\"exp\"\\s*:\\s*(\\d+)").find(decoded) ?: return null
        expMatch.groupValues[1].toLongOrNull()
    } catch (_: Throwable) {
        null
    }
}

private fun JsonPrimitive.asEpochSeconds(): Long? =
    longOrNull ?: contentOrNull?.toDoubleOrNull()?.toLong()

private fun sessionStillValid(session: StoredSupabaseSession, skewSeconds: Long = 120): Boolean {
    val exp = session.expiresAt ?: jwtExpEpochSeconds(session.accessToken) ?: return true
    val now = (js("Date.now()") as Double).toLong() / 1000L
    return now + skewSeconds < exp
}

private fun parseAuthPayload(jsonText: String): StoredSupabaseSession? {
    val root = try {
        jsonFormat.parseToJsonElement(jsonText).jsonObject
    } catch (_: Throwable) {
        return null
    }
    val err = root["error"]?.jsonPrimitive?.contentOrNull
    if (!err.isNullOrBlank()) return null

    val sessionObj = root["session"]?.jsonObject
    val tokenSource = sessionObj ?: root

    val access = tokenSource["access_token"]?.jsonPrimitive?.contentOrNull ?: return null
    val refresh = tokenSource["refresh_token"]?.jsonPrimitive?.contentOrNull.orEmpty()
    val expiresAt = tokenSource["expires_at"]?.jsonPrimitive?.asEpochSeconds()
        ?: jwtExpEpochSeconds(access)

    val userId = sessionObj?.get("user")?.jsonObject?.get("id")?.jsonPrimitive?.contentOrNull
        ?: root["user"]?.jsonObject?.get("id")?.jsonPrimitive?.contentOrNull
        ?: decodeJwtSub(access)
        ?: return null

    return StoredSupabaseSession(
        accessToken = access,
        refreshToken = refresh,
        expiresAt = expiresAt,
        userId = userId,
    )
}

private fun sessionFromImplicitHashParams(params: Map<String, String>): StoredSupabaseSession? {
    val access = params["access_token"] ?: return null
    val refresh = params["refresh_token"] ?: return null
    val expiresAt = params["expires_at"]?.toLongOrNull()
        ?: jwtExpEpochSeconds(access)
    val userId = decodeJwtSub(access) ?: return null
    return StoredSupabaseSession(
        accessToken = access,
        refreshToken = refresh,
        expiresAt = expiresAt,
        userId = userId,
    )
}

private fun stripAuthFromUrl() {
    val rawSearch = window.location.search.removePrefix("?").trim()
    val keptQuery =
        if (rawSearch.isBlank()) {
            ""
        } else {
            rawSearch
                .split("&")
                .mapNotNull { pair ->
                    if (pair.isBlank()) return@mapNotNull null
                    val idx = pair.indexOf('=')
                    val key = (if (idx >= 0) pair.substring(0, idx) else pair).trim().lowercase()
                    when (key) {
                        "access_token",
                        "refresh_token",
                        "token_hash",
                        "type",
                        "expires_at",
                        "expires_in",
                        "flow",
                        "code",
                        -> null
                        else -> pair
                    }
                }
                .joinToString("&")
        }
    val search = if (keptQuery.isBlank()) "" else "?$keptQuery"
    val path = window.location.pathname + search
    window.history.replaceState(window.history.state, "", path)
}

/**
 * Échange `token_hash` + `type` (lien e-mail / template personnalisé), comme `verifyOtp` côté auth-js.
 */
private suspend fun verifySupabaseTokenHash(
    client: HttpClient,
    base: String,
    anon: String,
    tokenHash: String,
    type: String,
): StoredSupabaseSession? {
    val body = buildJsonObject {
        put("type", type)
        put("token_hash", tokenHash)
        put("gotrue_meta_security", buildJsonObject {})
    }
    return try {
        val response = client.post("$base/auth/v1/verify") {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer $anon")
            header("X-Supabase-Api-Version", "2024-01-01")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), body))
        }
        val text = response.body<String>()
        parseAuthPayload(text)
    } catch (_: Throwable) {
        null
    }
}

/**
 * Après clic sur le lien : fragment implicite (#access_token=…), `token_hash`+`type`, jetons en query,
 * ou flux PKCE (?code= + code_verifier local, anciens envois uniquement).
 */
suspend fun consumeSupabaseAuthRedirect(
    client: HttpClient,
    allowRecoveryConsumption: Boolean = true,
) {
    if (!isSupabaseWebConfigured()) return
    val base = supabaseWebUrl() ?: return
    val anon = supabaseWebAnonKey() ?: return

    val hash = window.location.hash.removePrefix("#").trim()
    if (hash.isNotBlank()) {
        val params = parseQueryToMap(hash)
        val hashType = params["type"]?.trim()?.lowercase()
        if (hashType == "recovery" && !allowRecoveryConsumption) return
        val session = sessionFromImplicitHashParams(params)
        if (session != null) {
            savePersistedSession(session)
            if (hashType == "recovery") {
                localStorage.setItem(RECOVERY_PENDING_KEY, "1")
                localStorage.setItem(RECOVERY_UI_LOCK_KEY, "1")
            } else if (hashType == "signup") {
                markOpenParentProfileOncePending()
            }
            stripAuthFromUrl()
            return
        }
    }

    val search = window.location.search.removePrefix("?").trim()
    if (search.isBlank()) return
    val params = parseQueryToMap(search)
    val tokenHash = params["token_hash"]?.trim()?.takeIf { it.isNotEmpty() }
    val verifyType = params["type"]?.trim()?.takeIf { it.isNotEmpty() }
    if (verifyType?.lowercase() == "recovery" && !allowRecoveryConsumption) return
    if (tokenHash != null && verifyType != null) {
        val session = verifySupabaseTokenHash(client, base, anon, tokenHash, verifyType)
        if (session != null) {
            savePersistedSession(session)
            if (verifyType.lowercase() == "recovery") {
                localStorage.setItem(RECOVERY_PENDING_KEY, "1")
                localStorage.setItem(RECOVERY_UI_LOCK_KEY, "1")
            } else if (verifyType.lowercase() == "signup") {
                markOpenParentProfileOncePending()
            }
            stripAuthFromUrl()
            return
        }
    }

    val querySession = sessionFromImplicitHashParams(params)
    if (querySession != null) {
        savePersistedSession(querySession)
        if (verifyType?.lowercase() == "recovery") {
            localStorage.setItem(RECOVERY_PENDING_KEY, "1")
            localStorage.setItem(RECOVERY_UI_LOCK_KEY, "1")
        } else if (verifyType?.lowercase() == "signup") {
            markOpenParentProfileOncePending()
        }
        stripAuthFromUrl()
        return
    }

    val code = params["code"]?.trim()?.takeIf { it.isNotEmpty() } ?: return
    val verifier = localStorage.getItem(PKCE_VERIFIER_KEY) ?: return
    val body = buildJsonObject {
        put("auth_code", code)
        put("code_verifier", verifier)
    }
    val url = "$base/auth/v1/token?grant_type=pkce"
    val response = client.post(url) {
        contentType(ContentType.Application.Json)
        header("apikey", anon)
        header("Authorization", "Bearer $anon")
        header("X-Supabase-Api-Version", "2024-01-01")
        setBody(jsonFormat.encodeToString(JsonObject.serializer(), body))
    }
    val text = response.body<String>()
    val session = parseAuthPayload(text)
    if (session != null) {
        savePersistedSession(session)
        localStorage.removeItem(PKCE_VERIFIER_KEY)
        stripAuthFromUrl()
    }
}

/**
 * Envoie un lien magique en flux **implicite** (sans PKCE), pour que le lien fonctionne même si l’utilisateur
 * l’ouvre dans un autre navigateur ou depuis l’app Mail (le PKCE exige le même stockage que la demande).
 *
 * @param createUser si `true`, GoTrue peut créer un compte pour une adresse inconnue ; si `false`, réservé aux comptes existants.
 * @return null si OK, sinon message d’erreur
 */
suspend fun sendSupabaseMagicLinkEmail(
    client: HttpClient,
    email: String,
    createUser: Boolean = true,
): String? {
    if (!isSupabaseWebConfigured()) {
        return "Supabase n’est pas configuré sur cette page."
    }
    val trimmed = email.trim()
    if (trimmed.isEmpty() || !trimmed.contains('@')) {
        return "Merci d’indiquer une adresse e-mail valide."
    }
    if (createUser) {
        blockedSignupEmailMessage(trimmed)?.let { return it }
    }
    val base = supabaseWebUrl() ?: return "Configuration Supabase incomplète."
    val anon = supabaseWebAnonKey() ?: return "Configuration Supabase incomplète."

    // Comme auth-js : `redirect_to` en query (pas `email_redirect_to` dans un objet `options` du corps).
    val redirectTo = "${window.location.origin}${window.location.pathname}"
    val redirectEnc = js("encodeURIComponent")(redirectTo).unsafeCast<String>()

    val payload = buildJsonObject {
        put("email", trimmed)
        put("create_user", createUser)
        put("data", buildJsonObject {})
        put("gotrue_meta_security", buildJsonObject {})
    }

    val url = "$base/auth/v1/otp?redirect_to=$redirectEnc"
    return try {
        val response = client.post(url) {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer $anon")
            header("X-Supabase-Api-Version", "2024-01-01")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), payload))
        }
        val text = response.body<String>()
        if (response.status.value in 200..299) {
            null
        } else {
            val err = try {
                val obj = jsonFormat.parseToJsonElement(text).jsonObject
                obj["error_description"]?.jsonPrimitive?.contentOrNull
                    ?: obj["msg"]?.jsonPrimitive?.contentOrNull
                    ?: obj["message"]?.jsonPrimitive?.contentOrNull
                    ?: obj["error"]?.jsonPrimitive?.contentOrNull
            } catch (_: Throwable) {
                null
            }
            err ?: "Envoi impossible (${response.status.value}). Réessaie plus tard."
        }
    } catch (e: Throwable) {
        "Erreur réseau : ${e.message ?: "inconnue"}"
    }
}

/** Résultat d’une inscription par mot de passe (GoTrue). */
sealed class SupabasePasswordSignUpResult {
    data object LoggedIn : SupabasePasswordSignUpResult()
    data object AwaitingEmailConfirmation : SupabasePasswordSignUpResult()
    data class Failure(val message: String) : SupabasePasswordSignUpResult()
}

private fun authApiErrorMessage(text: String, fallback: String): String {
    val raw = try {
        val obj = jsonFormat.parseToJsonElement(text).jsonObject
        obj["error_description"]?.jsonPrimitive?.contentOrNull
            ?: obj["msg"]?.jsonPrimitive?.contentOrNull
            ?: obj["message"]?.jsonPrimitive?.contentOrNull
            ?: obj["error"]?.jsonPrimitive?.contentOrNull
            ?: fallback
    } catch (_: Throwable) {
        fallback
    }
    return toFrenchUserFacingMessage(raw, fallback)
}

private fun signInApiErrorMessage(text: String, fallback: String): String {
    val raw = try {
        val obj = jsonFormat.parseToJsonElement(text).jsonObject
        obj["error_description"]?.jsonPrimitive?.contentOrNull
            ?: obj["msg"]?.jsonPrimitive?.contentOrNull
            ?: obj["message"]?.jsonPrimitive?.contentOrNull
            ?: obj["error"]?.jsonPrimitive?.contentOrNull
            ?: fallback
    } catch (_: Throwable) {
        fallback
    }
    val lower = raw.lowercase()
    return when {
        "email not confirmed" in lower || "confirm" in lower ->
            "Ton e-mail n'est pas encore confirmé. Ouvre le lien reçu par mail puis réessaie."
        "invalid login" in lower || "invalid credentials" in lower || "invalid_grant" in lower ->
            "E-mail ou mot de passe incorrect."
        "network" in lower || "timeout" in lower || "fetch" in lower || "socket" in lower ->
            "Erreur réseau. Réessaie dans un instant."
        else -> authApiErrorMessage(text, fallback)
    }
}

private const val RECOVERY_SESSION_LOST_FR =
    "La session de réinitialisation a expiré ou a été interrompue (rechargement, autre onglet, délai trop long). " +
        "Demande un nouveau code via « Mot de passe oublié », saisis le code reçu, puis choisis ton mot de passe."

private fun resetPasswordErrorMessage(text: String, fallback: String, sbErrorHeader: String? = null): String {
    val obj = runCatching { jsonFormat.parseToJsonElement(text).jsonObject }.getOrNull()
    val raw = try {
        obj?.let { o ->
            o["error_description"]?.jsonPrimitive?.contentOrNull
                ?: o["msg"]?.jsonPrimitive?.contentOrNull
                ?: o["message"]?.jsonPrimitive?.contentOrNull
                ?: o["error"]?.jsonPrimitive?.contentOrNull
        }
    } catch (_: Throwable) {
        null
    } ?: fallback
    val codeBits = buildList {
        sbErrorHeader?.trim()?.takeIf { it.isNotEmpty() }?.let { add(it) }
        obj?.get("code")?.jsonPrimitive?.contentOrNull?.let { add(it) }
        obj?.get("error_code")?.jsonPrimitive?.contentOrNull?.let { add(it) }
    }.joinToString(" ")
    val normalized = "$codeBits $raw".lowercase()
    val looksLikeSamePassword =
        "same_password" in normalized ||
            "different from the old" in normalized ||
            "different from your old" in normalized ||
            ("must be different" in normalized && "password" in normalized) ||
            ("cannot reuse" in normalized && "password" in normalized) ||
            ("reuse" in normalized && "password" in normalized) ||
            ("same" in normalized && "password" in normalized)
    val looksLikeSessionProblem =
        "unauthorized" in normalized ||
            "forbidden" in normalized ||
            "invalid_grant" in normalized ||
            "invalid jwt" in normalized ||
            "jwt expired" in normalized ||
            "not authenticated" in normalized ||
            "refresh token" in normalized ||
            ("expired" in normalized && "token" in normalized) ||
            ("session" in normalized && ("expired" in normalized || "invalid" in normalized))
    val looksLikeOtpProblem =
        "otp" in normalized ||
            "token_hash" in normalized ||
            "email token" in normalized
    val looksLikeOtpOrVerifyFailure =
        "otp_expired" in normalized ||
            looksLikeOtpProblem ||
            ("invalid" in normalized && ("otp" in normalized || "token" in normalized || "verification" in normalized)) ||
            ("expired" in normalized && "otp" in normalized)
    return when {
        looksLikeSamePassword ->
            "Le nouveau mot de passe doit être différent de l'ancien. Choisis un autre mot de passe."
        "password" in normalized && "weak" in normalized ->
            "Le nouveau mot de passe est trop faible. Choisis un mot de passe plus robuste."
        looksLikeSessionProblem ->
            RECOVERY_SESSION_LOST_FR
        looksLikeOtpOrVerifyFailure ->
            "Code de vérification invalide ou expiré. Demande un nouveau code puis réessaie."
        else ->
            authApiErrorMessage(text, fallback)
    }
}

/**
 * Connexion par e-mail + mot de passe (tous navigateurs).
 * @return null si session enregistrée ; sinon message d’erreur.
 */
suspend fun signInSupabaseWithPassword(
    client: HttpClient,
    email: String,
    password: String,
): String? {
    if (!isSupabaseWebConfigured()) {
        return "Supabase n’est pas configuré sur cette page."
    }
    val trimmed = email.trim()
    if (trimmed.isEmpty() || !trimmed.contains('@')) {
        return "Merci d’indiquer une adresse e-mail valide."
    }
    if (password.isEmpty()) {
        return "Merci d’indiquer ton mot de passe."
    }
    val base = supabaseWebUrl() ?: return "Configuration Supabase incomplète."
    val anon = supabaseWebAnonKey() ?: return "Configuration Supabase incomplète."
    val url = "$base/auth/v1/token?grant_type=password"
    val payload = buildJsonObject {
        put("email", trimmed)
        put("password", password)
        put("gotrue_meta_security", buildJsonObject {})
    }
    return try {
        val response = client.post(url) {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer $anon")
            header("X-Supabase-Api-Version", "2024-01-01")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), payload))
        }
        val text = response.body<String>()
        if (response.status.value in 200..299) {
            val session = parseAuthPayload(text)
            if (session != null) {
                savePersistedSession(session)
                localStorage.removeItem(PASSKEY_REQUIRES_PASSWORD_LOGIN_KEY)
                null
            } else {
                authApiErrorMessage(text, "Connexion impossible.")
            }
        } else {
            signInApiErrorMessage(text, "E-mail ou mot de passe incorrect.")
        }
    } catch (e: Throwable) {
        toFrenchUserFacingMessage(e.message, "Erreur réseau. Réessaie dans un instant.")
    }
}

/**
 * Inscription par e-mail + mot de passe.
 */
suspend fun signUpSupabaseWithPassword(
    client: HttpClient,
    email: String,
    password: String,
): SupabasePasswordSignUpResult {
    if (!isSupabaseWebConfigured()) {
        return SupabasePasswordSignUpResult.Failure("Supabase n’est pas configuré sur cette page.")
    }
    val trimmed = email.trim()
    if (trimmed.isEmpty() || !trimmed.contains('@')) {
        return SupabasePasswordSignUpResult.Failure("Merci d’indiquer une adresse e-mail valide.")
    }
    blockedSignupEmailMessage(trimmed)?.let {
        return SupabasePasswordSignUpResult.Failure(it)
    }
    if (password.length < 6) {
        return SupabasePasswordSignUpResult.Failure("Le mot de passe doit contenir au moins 6 caractères.")
    }
    val base = supabaseWebUrl() ?: return SupabasePasswordSignUpResult.Failure("Configuration Supabase incomplète.")
    val anon = supabaseWebAnonKey() ?: return SupabasePasswordSignUpResult.Failure("Configuration Supabase incomplète.")
    val payload = buildJsonObject {
        put("email", trimmed)
        put("password", password)
        put("data", buildJsonObject {})
        put("gotrue_meta_security", buildJsonObject {})
    }
    return try {
        val response = client.post("$base/auth/v1/signup") {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer $anon")
            header("X-Supabase-Api-Version", "2024-01-01")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), payload))
        }
        val text = response.body<String>()
        if (response.status.value in 200..299) {
            val session = parseAuthPayload(text)
            if (session != null) {
                savePersistedSession(session)
                SupabasePasswordSignUpResult.LoggedIn
            } else {
                SupabasePasswordSignUpResult.AwaitingEmailConfirmation
            }
        } else {
            val msg = authApiErrorMessage(text, "Inscription impossible (${response.status.value}).")
            SupabasePasswordSignUpResult.Failure(msg)
        }
    } catch (e: Throwable) {
        SupabasePasswordSignUpResult.Failure(
            toFrenchUserFacingMessage(e.message, "Erreur réseau. Réessaie dans un instant."),
        )
    }
}

/**
 * Demande un e-mail de réinitialisation du mot de passe (lien Supabase).
 * @return null si la requête a été acceptée ; sinon message d’erreur.
 */
suspend fun sendSupabasePasswordResetEmail(client: HttpClient, email: String): String? {
    if (!isSupabaseWebConfigured()) {
        return "Supabase n’est pas configuré sur cette page."
    }
    val trimmed = email.trim().lowercase()
    if (trimmed.isEmpty() || !trimmed.contains('@')) {
        return "Merci d’indiquer une adresse e-mail valide."
    }
    val base = supabaseWebUrl() ?: return "Configuration Supabase incomplète."
    val anon = supabaseWebAnonKey() ?: return "Configuration Supabase incomplète."
    val redirectTo = "${window.location.origin}${window.location.pathname}?flow=reset_password"
    val redirectEnc = js("encodeURIComponent")(redirectTo).unsafeCast<String>()
    val payload = buildJsonObject {
        put("email", trimmed)
        put("gotrue_meta_security", buildJsonObject {})
    }
    val url = "$base/auth/v1/recover?redirect_to=$redirectEnc"
    return try {
        val response = client.post(url) {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer $anon")
            header("X-Supabase-Api-Version", "2024-01-01")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), payload))
        }
        val text = response.body<String>()
        if (response.status.value in 200..299) {
            // Dès qu'un reset est demandé, imposer une prochaine connexion classique.
            localStorage.setItem(PASSKEY_REQUIRES_PASSWORD_LOGIN_KEY, "1")
            null
        } else {
            authApiErrorMessage(text, "Envoi impossible (${response.status.value}). Réessaie plus tard.")
        }
    } catch (e: Throwable) {
        toFrenchUserFacingMessage(e.message, "Erreur réseau. Réessaie dans un instant.")
    }
}

/**
 * Vérifie un code OTP de réinitialisation (type `recovery`) puis ouvre une session temporaire
 * permettant l'appel `auth/v1/user` pour définir le nouveau mot de passe.
 * @return null si OK ; sinon message d'erreur utilisateur.
 */
suspend fun verifySupabaseRecoveryOtpCode(
    client: HttpClient,
    email: String,
    otpCode: String,
): String? {
    if (!isSupabaseWebConfigured()) return "Supabase n’est pas configuré sur cette page."
    val trimmedEmail = email.trim().lowercase()
    if (trimmedEmail.isEmpty() || !trimmedEmail.contains('@')) {
        return "Merci d’indiquer une adresse e-mail valide."
    }
    val (extracted, preferredKind) = extractRecoveryInput(otpCode)
    val code =
        if (preferredKind == null) {
            extracted.filter { it.isDigit() }
        } else {
            extracted
        }
    if (preferredKind == null) {
        if (code.length != SUPABASE_WEB_EMAIL_OTP_DIGITS) {
            return "Le code doit contenir $SUPABASE_WEB_EMAIL_OTP_DIGITS chiffres (comme dans l’e-mail de réinitialisation)."
        }
    } else if (code.length < 6) {
        return "Le code de vérification semble incomplet."
    }
    val base = supabaseWebUrl() ?: return "Configuration Supabase incomplète."
    val anon = supabaseWebAnonKey() ?: return "Configuration Supabase incomplète."
    // GoTrue : avec token_hash, seuls type + token_hash sont autorisés (pas email/phone/redirect_to).
    suspend fun postRecoveryVerify(body: JsonObject): Pair<Boolean, String> {
        return try {
            val response = client.post("$base/auth/v1/verify") {
                contentType(ContentType.Application.Json)
                header("apikey", anon)
                header("Authorization", "Bearer $anon")
                header("X-Supabase-Api-Version", "2024-01-01")
                setBody(jsonFormat.encodeToString(JsonObject.serializer(), body))
            }
            val text = runCatching { response.body<String>() }.getOrNull().orEmpty()
            if (response.status.value !in 200..299) {
                val sbErr =
                    response.headers["x-sb-error-code"]
                        ?: response.headers["X-Sb-Error-Code"]
                val merged =
                    when {
                        !sbErr.isNullOrBlank() && text.isNotBlank() -> "$text x-sb-error-code:$sbErr"
                        !sbErr.isNullOrBlank() -> """{"error_code":"$sbErr"}"""
                        else -> text
                    }
                false to merged
            } else {
                val session = parseAuthPayload(text)
                if (session == null) {
                    false to text
                } else {
                    savePersistedSession(session)
                    localStorage.setItem(RECOVERY_PENDING_KEY, "1")
                    localStorage.setItem(RECOVERY_UI_LOCK_KEY, "1")
                    true to text
                }
            }
        } catch (e: Throwable) {
            false to (e.message ?: "")
        }
    }
    suspend fun attemptVerifyTokenHashOnly(): Pair<Boolean, String> {
        val body = buildJsonObject {
            put("type", "recovery")
            put("token_hash", code)
            put("gotrue_meta_security", buildJsonObject {})
        }
        return postRecoveryVerify(body)
    }
    suspend fun attemptVerifyOtpWithEmail(verifyType: String, emailForRequest: String): Pair<Boolean, String> {
        val body = buildJsonObject {
            put("type", verifyType)
            put("token", code)
            put("email", emailForRequest)
            put("gotrue_meta_security", buildJsonObject {})
        }
        return postRecoveryVerify(body)
    }

    // Même adresse en minuscules et telle que saisie (hash côté serveur = email + OTP).
    val emailsToTry = listOf(trimmedEmail, email.trim()).filter { it.contains('@') }.distinct()
    val errorChunks = mutableListOf<String>()

    suspend fun tryAllOtpByEmail(): Boolean {
        for (em in emailsToTry) {
            // "email" = EmailOTPVerification (GoTrue) : enchaîne contrôle signup puis recovery.
            for (verifyType in listOf("recovery", "email")) {
                val r = attemptVerifyOtpWithEmail(verifyType, em)
                if (r.first) return true
                errorChunks.add(r.second)
            }
        }
        return false
    }

    if (preferredKind == "token_hash") {
        val h = attemptVerifyTokenHashOnly()
        if (h.first) return null
        errorChunks.add(h.second)
        if (tryAllOtpByEmail()) return null
    } else {
        if (tryAllOtpByEmail()) return null
        val h = attemptVerifyTokenHashOnly()
        if (h.first) return null
        errorChunks.add(h.second)
    }

    val raw = errorChunks.joinToString(" ").lowercase()
    return if ("otp_expired" in raw) {
        "Ce code a expiré (délai dépassé côté serveur). " +
            "Demande un nouveau code via « Mot de passe oublié » et saisis-le tout de suite après réception."
    } else if ("expired" in raw || "invalid" in raw || "token" in raw || "otp" in raw) {
        "Code de vérification invalide ou expiré. " +
            "Demande un nouveau code via « Mot de passe oublié » puis réessaie."
    } else {
        val details = raw.replace(Regex("\\s+"), " ").trim().take(180)
        if (details.isBlank()) {
            "Vérification du code impossible pour le moment. Réessaie dans un instant."
        } else {
            "Vérification du code impossible: $details"
        }
    }
}

/**
 * Vérifie un code OTP de confirmation d'inscription (type `signup`).
 * @return null si OK ; sinon message d'erreur utilisateur.
 */
suspend fun verifySupabaseSignupOtpCode(
    client: HttpClient,
    email: String,
    otpCode: String,
): String? {
    if (!isSupabaseWebConfigured()) return "Supabase n’est pas configuré sur cette page."
    val trimmedEmail = email.trim()
    if (trimmedEmail.isEmpty() || !trimmedEmail.contains('@')) {
        return "Merci d’indiquer une adresse e-mail valide."
    }
    val code = otpCode.trim().filter { it.isDigit() }
    if (code.length != SUPABASE_WEB_EMAIL_OTP_DIGITS) {
        return "Le code de confirmation doit contenir $SUPABASE_WEB_EMAIL_OTP_DIGITS chiffres."
    }
    val base = supabaseWebUrl() ?: return "Configuration Supabase incomplète."
    val anon = supabaseWebAnonKey() ?: return "Configuration Supabase incomplète."
    val body = buildJsonObject {
        put("type", "signup")
        put("token", code)
        put("email", trimmedEmail)
        put("gotrue_meta_security", buildJsonObject {})
    }
    return try {
        val response = client.post("$base/auth/v1/verify") {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer $anon")
            header("X-Supabase-Api-Version", "2024-01-01")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), body))
        }
        val text = response.body<String>()
        if (response.status.value !in 200..299) {
            val sbErr = response.headers["x-sb-error-code"] ?: response.headers["X-Sb-Error-Code"]
            val lower = (text + " " + sbErr.orEmpty()).lowercase()
            if ("otp_expired" in lower) {
                "Ce code de confirmation a expiré. Relance l'inscription ou demande un nouveau code puis réessaie."
            } else if ("expired" in lower || "invalid" in lower || "token" in lower || "otp" in lower) {
                "Code de confirmation invalide ou expiré. Demande un nouveau code puis réessaie."
            } else {
                authApiErrorMessage(text, "Vérification du code impossible.")
            }
        } else {
            // Selon la config Supabase, une session peut être renvoyée ou non.
            val session = parseAuthPayload(text)
            if (session != null) savePersistedSession(session)
            null
        }
    } catch (e: Throwable) {
        toFrenchUserFacingMessage(e.message, "Erreur réseau. Réessaie dans un instant.")
    }
}

/**
 * Renvoie l'e-mail de confirmation d'inscription (flux lien e-mail).
 * @return null si la requête a été acceptée ; sinon message d'erreur.
 */
suspend fun resendSupabaseSignupConfirmationEmail(client: HttpClient, email: String): String? {
    if (!isSupabaseWebConfigured()) {
        return "Supabase n’est pas configuré sur cette page."
    }
    val trimmed = email.trim()
    if (trimmed.isEmpty() || !trimmed.contains('@')) {
        return "Merci d’indiquer une adresse e-mail valide."
    }
    val base = supabaseWebUrl() ?: return "Configuration Supabase incomplète."
    val anon = supabaseWebAnonKey() ?: return "Configuration Supabase incomplète."
    val payload = buildJsonObject {
        put("email", trimmed)
        put("type", "signup")
        put("gotrue_meta_security", buildJsonObject {})
    }
    return try {
        val response = client.post("$base/auth/v1/resend") {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer $anon")
            header("X-Supabase-Api-Version", "2024-01-01")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), payload))
        }
        val text = response.body<String>()
        if (response.status.value in 200..299) {
            null
        } else {
            authApiErrorMessage(text, "Envoi impossible (${response.status.value}). Réessaie plus tard.")
        }
    } catch (e: Throwable) {
        toFrenchUserFacingMessage(e.message, "Erreur réseau. Réessaie dans un instant.")
    }
}

suspend fun hasSupabaseUserSession(client: HttpClient): Boolean =
    ensureSupabaseSession(client) != null

/** E-mail du compte déjà connecté sur cet appareil (JWT), ou `null` s’il n’y a pas de session valide. */
suspend fun currentSupabaseAuthEmail(client: HttpClient): String? {
    val session = ensureSupabaseSession(client) ?: return null
    return decodeJwtEmail(session.accessToken)
}

/**
 * Indique si un compte Supabase Auth existe pour cet e-mail (**aucun** envoi d’e-mail).
 * Appelle l’Edge Function `check-auth-email` (migration `histoire_email_registered` + déploiement requis).
 * @return `null` si erreur réseau ou réponse inattendue ; `true` / `false` pour le résultat.
 */
suspend fun checkSupabaseAccountEmailRegistered(client: HttpClient, email: String): Boolean? {
    if (!isSupabaseWebConfigured()) return null
    val trimmed = email.trim()
    if (trimmed.isEmpty() || !trimmed.contains('@')) return false
    val base = supabaseWebUrl() ?: return null
    val anon = supabaseWebAnonKey() ?: return null
    val url = "$base/functions/v1/check-auth-email"
    return try {
        val payload = buildJsonObject { put("email", trimmed) }
        val response = client.post(url) {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer $anon")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), payload))
        }
        val text = response.body<String>()
        if (response.status.value !in 200..299) return null
        val root = try {
            jsonFormat.parseToJsonElement(text).jsonObject
        } catch (_: Throwable) {
            return null
        }
        root["exists"]?.jsonPrimitive?.contentOrNull?.toBooleanStrictOrNull()
    } catch (_: Throwable) {
        null
    }
}

private suspend fun refreshSession(
    client: HttpClient,
    supabaseUrl: String,
    anonKey: String,
    refreshToken: String,
): StoredSupabaseSession? {
    val url = "$supabaseUrl/auth/v1/token?grant_type=refresh_token"
    val refreshJson = buildJsonObject {
        put("refresh_token", refreshToken)
    }
    val body = jsonFormat.encodeToString(JsonObject.serializer(), refreshJson)
    val response = client.post(url) {
        contentType(ContentType.Application.Json)
        header("apikey", anonKey)
        header("Authorization", "Bearer $anonKey")
        setBody(body)
    }
    val text = response.body<String>()
    return parseAuthPayload(text)
}

private suspend fun isAccessTokenStillValidOnServer(
    client: HttpClient,
    supabaseUrl: String,
    anonKey: String,
    accessToken: String,
): Boolean {
    if (accessToken.isBlank()) return false
    return try {
        val response = client.get("$supabaseUrl/auth/v1/user") {
            header("apikey", anonKey)
            header("Authorization", "Bearer $accessToken")
            header("X-Supabase-Api-Version", "2024-01-01")
        }
        response.status.value in 200..299
    } catch (_: Throwable) {
        false
    }
}

private suspend fun ensureSupabaseSession(client: HttpClient): StoredSupabaseSession? {
    val base = supabaseWebUrl() ?: return null
    val anon = supabaseWebAnonKey() ?: return null

    consumeSupabaseAuthRedirect(client)

    loadPersistedSession()?.let { existing ->
        if (sessionStillValid(existing) && isAccessTokenStillValidOnServer(client, base, anon, existing.accessToken)) {
            return existing
        }
        if (existing.refreshToken.isNotBlank()) {
            val refreshed = refreshSession(client, base, anon, existing.refreshToken)
            if (refreshed != null && isAccessTokenStillValidOnServer(client, base, anon, refreshed.accessToken)) {
                savePersistedSession(refreshed)
                return refreshed
            }
        }
        savePersistedSession(null)
    }

    return null
}

suspend fun fetchChildProfilesFromSupabase(client: HttpClient): List<StoredChildProfile>? {
    if (!isSupabaseWebConfigured()) return null
    return try {
        val base = supabaseWebUrl() ?: return null
        val anon = supabaseWebAnonKey() ?: return null
        val session = ensureSupabaseSession(client) ?: return null

        val url = "$base/rest/v1/child_profiles?select=id,first_name,birth_date&order=sort_order.asc"
        val response = client.get(url) {
            header("apikey", anon)
            header("Authorization", "Bearer ${session.accessToken}")
        }
        val text = response.body<String>()
        if (text.isBlank() || text == "[]") {
            emptyList()
        } else {
            jsonFormat.decodeFromString(ListSerializer(ChildProfileRow.serializer()), text).map { row ->
                val iso = row.birthDate.trim().substringBefore('T').take(10)
                StoredChildProfile(id = row.id, firstName = row.firstName, birthDateIso = iso)
            }
        }
    } catch (_: Throwable) {
        null
    }
}

suspend fun replaceChildProfilesOnSupabase(client: HttpClient, profiles: List<StoredChildProfile>): Boolean {
    if (!isSupabaseWebConfigured() || profiles.isEmpty()) return false
    return try {
        val base = supabaseWebUrl() ?: return false
        val anon = supabaseWebAnonKey() ?: return false
        val session = ensureSupabaseSession(client) ?: return false

        val deleteUrl = "$base/rest/v1/child_profiles?user_id=eq.${session.userId}"
        client.delete(deleteUrl) {
            header("apikey", anon)
            header("Authorization", "Bearer ${session.accessToken}")
        }

        val rows = profiles.mapIndexed { index, p ->
            val id = if (looksLikeUuid(p.id)) p.id else newChildProfileId()
            ChildProfileInsert(
                id = id,
                userId = session.userId,
                firstName = p.firstName.trim(),
                birthDate = p.birthDateIso.trim().substringBefore('T').take(10),
                sortOrder = index,
            )
        }
        val insertArray = buildJsonArray {
            rows.forEach { row ->
                add(jsonFormat.encodeToJsonElement(ChildProfileInsert.serializer(), row))
            }
        }
        val payload = jsonFormat.encodeToString(JsonArray.serializer(), insertArray)

        val insertUrl = "$base/rest/v1/child_profiles"
        client.post(insertUrl) {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer ${session.accessToken}")
            header("Prefer", "return=minimal")
            setBody(payload)
        }

        val normalized = rows.zip(profiles).map { (insert, original) ->
            StoredChildProfile(
                id = insert.id,
                firstName = original.firstName.trim(),
                birthDateIso = original.birthDateIso.trim().substringBefore('T').take(10),
            )
        }
        saveStoredChildProfiles(normalized)
        true
    } catch (_: Throwable) {
        false
    }
}

data class UserProfileOnboardingInfo(
    val needsParentIdentityForm: Boolean,
    val initialFirstName: String,
    val initialLastName: String,
    /** Prérempli (JWT ou colonne `contact_email`) pour la saisie. */
    val initialContactEmail: String,
)

/**
 * Détermine si l’écran « identité parent » doit s’afficher (champs vides en base).
 */
suspend fun loadUserProfileOnboardingInfo(client: HttpClient): UserProfileOnboardingInfo {
    if (!isSupabaseWebConfigured()) {
        return UserProfileOnboardingInfo(needsParentIdentityForm = false, "", "", "")
    }
    val session = ensureSupabaseSession(client) ?: return UserProfileOnboardingInfo(false, "", "", "")
    val jwtEmail = decodeJwtEmail(session.accessToken).orEmpty()
    return try {
        val base = supabaseWebUrl() ?: return UserProfileOnboardingInfo(false, "", "", "")
        val anon = supabaseWebAnonKey() ?: return UserProfileOnboardingInfo(false, "", "", "")
        val url =
            "$base/rest/v1/user_profile?user_id=eq.${session.userId}&select=first_name,last_name,contact_email"
        val response = client.get(url) {
            header("apikey", anon)
            header("Authorization", "Bearer ${session.accessToken}")
        }
        val text = response.body<String>()
        val row: UserProfileFetchRow? =
            if (text.isBlank() || text == "[]") {
                null
            } else {
                jsonFormat.decodeFromString(ListSerializer(UserProfileFetchRow.serializer()), text).firstOrNull()
            }
        val fn = row?.firstName?.trim().orEmpty()
        val ln = row?.lastName?.trim().orEmpty()
        val dbEmail = row?.contactEmail?.trim().orEmpty()
        val needs = fn.isBlank() || ln.isBlank() || dbEmail.isBlank()
        val emailHint = dbEmail.ifBlank { jwtEmail }
        UserProfileOnboardingInfo(
            needsParentIdentityForm = needs,
            initialFirstName = fn,
            initialLastName = ln,
            initialContactEmail = emailHint,
        )
    } catch (_: Throwable) {
        UserProfileOnboardingInfo(false, "", "", jwtEmail)
    }
}

/** Jeton Supabase (parcours web) pour que la Pages Function lise `groq_api_key` côté serveur — jamais la clé Groq dans le navigateur. */
suspend fun supabaseAccessTokenForStory(client: HttpClient): String? {
    if (!isSupabaseWebConfigured()) return null
    return ensureSupabaseSession(client)?.accessToken?.trim()?.takeIf { it.isNotEmpty() }
}

suspend fun saveUserProfileIdentity(
    client: HttpClient,
    firstName: String,
    lastName: String,
    contactEmail: String,
): Boolean {
    if (!isSupabaseWebConfigured()) return false
    return try {
        val base = supabaseWebUrl() ?: return false
        val anon = supabaseWebAnonKey() ?: return false
        val session = ensureSupabaseSession(client) ?: return false
        val fn = firstName.trim()
        val ln = lastName.trim()
        val em = contactEmail.trim()
        if (fn.isEmpty() || ln.isEmpty() || em.isEmpty()) return false
        val display = "$fn $ln".trim()
        val body = buildJsonObject {
            put("first_name", fn)
            put("last_name", ln)
            put("contact_email", em)
            put("display_name", display)
        }
        val patchUrl = "$base/rest/v1/user_profile?user_id=eq.${session.userId}"
        val response = client.patch(patchUrl) {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer ${session.accessToken}")
            header("Prefer", "return=minimal")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), body))
        }
        if (response.status.value !in 200..299) return false

        // Miroir côté Supabase Auth (auth.users raw_user_meta_data) pour affichage display_name
        // dans Authentication > Users.
        val authBody = buildJsonObject {
            put(
                "data",
                buildJsonObject {
                    put("display_name", display)
                    put("first_name", fn)
                    put("last_name", ln)
                },
            )
        }
        val authResp = client.put("$base/auth/v1/user") {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer ${session.accessToken}")
            header("X-Supabase-Api-Version", "2024-01-01")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), authBody))
        }
        authResp.status.value in 200..299
    } catch (_: Throwable) {
        false
    }
}

fun looksLikeUuid(value: String): Boolean {
    val v = value.trim()
    return v.length == 36 && v.count { it == '-' } == 4
}

/**
 * Indique qu'un lien de récupération vient d'être consommé côté web.
 * Le flag est consommé (lecture unique) pour afficher le formulaire de nouveau mot de passe.
 */
fun consumeRecoveryPendingFlag(): Boolean {
    val raw = localStorage.getItem(RECOVERY_PENDING_KEY)?.trim().orEmpty()
    localStorage.removeItem(RECOVERY_PENDING_KEY)
    return raw == "1"
}

/** Vérifie sans consommer le flag "lien recovery ouvert". */
fun hasRecoveryPendingFlag(): Boolean =
    localStorage.getItem(RECOVERY_PENDING_KEY)?.trim() == "1"

/** Verrou global : tant qu'il est actif, l'app doit rester sur le flux reset password. */
fun hasRecoveryUiLock(): Boolean =
    localStorage.getItem(RECOVERY_UI_LOCK_KEY)?.trim() == "1"

/** Nettoie tous les indicateurs locaux du flux de réinitialisation. */
fun clearRecoveryFlowFlags() {
    localStorage.removeItem(RECOVERY_PENDING_KEY)
    localStorage.removeItem(RECOVERY_UI_LOCK_KEY)
}

/** True if l'URL contient encore un jeton / flux recovery à consommer (ne pas effacer l'état local). */
private fun urlHasRecoveryAuthPayload(): Boolean {
    val h = window.location.hash.removePrefix("#").lowercase()
    val s = window.location.search.removePrefix("?").lowercase()
    val all = "$h&$s"
    if ("type=recovery" in all) return true
    if ("access_token=" in h && "type=recovery" in h) return true
    return false
}

private fun stripResetPasswordFlowQueryFromUrl() {
    val raw = window.location.search.removePrefix("?").trim()
    if (raw.isBlank()) return
    val kept =
        raw.split("&").mapNotNull { pair ->
            if (pair.isBlank()) return@mapNotNull null
            val key = pair.substringBefore("=").trim().lowercase()
            val value = pair.substringAfter("=", "").trim().lowercase()
            if (key == "flow" && value == "reset_password") return@mapNotNull null
            pair
        }.joinToString("&")
    val newSearch = if (kept.isBlank()) "" else "?$kept"
    val path = window.location.pathname + newSearch + window.location.hash
    window.history.replaceState(window.history.state, "", path)
}

/**
 * Rechargement (F5) : sans jeton recovery dans l'URL, les drapeaux locaux du formulaire
 * « nouveau mot de passe » ne doivent pas forcer cet écran (ex. navigation privée, onglet rechargé).
 */
fun resetStaleRecoveryClientStateOnReload() {
    val nav =
        runCatching {
            js(
                """(function(){
                    var e = (performance && performance.getEntriesByType) ? performance.getEntriesByType('navigation') : null;
                    if (e && e.length > 0 && e[0] && e[0].type) return String(e[0].type);
                    return '';
                })()""",
            ).unsafeCast<String>().lowercase()
        }.getOrDefault("")
    val legacyReload =
        runCatching {
            js("(performance && performance.navigation && performance.navigation.type === 1)").unsafeCast<Boolean>()
        }.getOrDefault(false)
    if (nav != "reload" && !legacyReload) return
    if (urlHasRecoveryAuthPayload()) return
    // Session temporaire après verify OTP : si on enlève les drapeaux recovery sans déconnecter,
    // le rechargement ouvrait l'atelier (connecté) au lieu de l'écran d'accueil auth.
    val hadRecoveryMarkers = hasRecoveryUiLock() || hasRecoveryPendingFlag()
    clearRecoveryFlowFlags()
    stripResetPasswordFlowQueryFromUrl()
    if (hadRecoveryMarkers) {
        clearSupabaseWebSession()
    }
}

/**
 * Met à jour le mot de passe après ouverture du lien "mot de passe oublié".
 * @return null si OK ; sinon message d'erreur utilisateur.
 */
suspend fun updateSupabasePasswordAfterRecovery(client: HttpClient, newPassword: String): String? {
    if (!isSupabaseWebConfigured()) {
        return "Supabase n’est pas configuré sur cette page."
    }
    if (newPassword.length < 6) {
        return "Le nouveau mot de passe doit contenir au moins 6 caractères."
    }
    val base = supabaseWebUrl() ?: return "Configuration Supabase incomplète."
    val anon = supabaseWebAnonKey() ?: return "Configuration Supabase incomplète."
    val session = ensureSupabaseSession(client) ?: return RECOVERY_SESSION_LOST_FR

    val payload = buildJsonObject {
        put("password", newPassword)
    }
    return try {
        val response = client.put("$base/auth/v1/user") {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer ${session.accessToken}")
            header("X-Supabase-Api-Version", "2024-01-01")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), payload))
        }
        val text = response.body<String>()
        if (response.status.value in 200..299) {
            clearSupabaseWebSession()
            localStorage.removeItem(RECOVERY_UI_LOCK_KEY)
            localStorage.setItem(PASSKEY_REQUIRES_PASSWORD_LOGIN_KEY, "1")
            null
        } else {
            val sbErr = response.headers["x-sb-error-code"] ?: response.headers["X-Sb-Error-Code"]
            resetPasswordErrorMessage(text, "Mise à jour du mot de passe impossible.", sbErr)
        }
    } catch (e: ClientRequestException) {
        val raw = runCatching { e.response.body<String>() }.getOrNull().orEmpty()
        val sbErr = e.response.headers["x-sb-error-code"] ?: e.response.headers["X-Sb-Error-Code"]
        resetPasswordErrorMessage(raw, "Mise à jour du mot de passe impossible.", sbErr)
    } catch (e: ServerResponseException) {
        val raw = runCatching { e.response.body<String>() }.getOrNull().orEmpty()
        val sbErr = e.response.headers["x-sb-error-code"] ?: e.response.headers["X-Sb-Error-Code"]
        resetPasswordErrorMessage(raw, "Le serveur rencontre un problème temporaire. Réessaie dans un instant.", sbErr)
    } catch (e: ResponseException) {
        val raw = runCatching { e.response.body<String>() }.getOrNull().orEmpty()
        val sbErr = e.response.headers["x-sb-error-code"] ?: e.response.headers["X-Sb-Error-Code"]
        resetPasswordErrorMessage(raw, "Mise à jour du mot de passe impossible.", sbErr)
    } catch (e: Throwable) {
        val msg = e.message.orEmpty().lowercase()
        if ("network" in msg || "timeout" in msg || "fetch" in msg || "socket" in msg) {
            "Erreur réseau. Réessaie dans un instant."
        } else {
            RECOVERY_SESSION_LOST_FR
        }
    }
}

suspend fun enrollPasskeyForCurrentUser(client: HttpClient): String? {
    if (!isSupabaseWebConfigured()) return "Supabase n’est pas configuré sur cette page."
    if (isPasskeyBlockedUntilPasswordLogin()) {
        return "La biométrie sera disponible après une connexion classique e-mail + mot de passe."
    }
    if (!isPasskeyEnrollmentSupported()) {
        return "Cet appareil ne supporte pas encore le déverrouillage biométrique sur ce navigateur."
    }
    val session = ensureSupabaseSession(client) ?: return "Connecte-toi d'abord avec e-mail et mot de passe."
    val base = supabaseWebUrl() ?: return "Configuration Supabase incomplète."
    val anon = supabaseWebAnonKey() ?: return "Configuration Supabase incomplète."

    val beginPayload = buildJsonObject { put("action", "begin_register") }
    val beginResponseText = try {
        val response = client.post("$base/functions/v1/webauthn-passkey") {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer ${session.accessToken}")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), beginPayload))
        }
        val text = response.body<String>()
        if (response.status.value !in 200..299) {
            val msg = runCatching {
                val obj = jsonFormat.parseToJsonElement(text).jsonObject
                obj["details"]?.jsonPrimitive?.contentOrNull
                    ?: obj["error"]?.jsonPrimitive?.contentOrNull
                    ?: text.take(220)
            }.getOrNull().orEmpty()
            return "Activation biométrique impossible: ${msg.ifBlank { "réponse serveur invalide" }}"
        }
        text
    } catch (_: Throwable) {
        return "Activation biométrique impossible pour le moment."
    }

    val beginObj = runCatching { jsonFormat.parseToJsonElement(beginResponseText).jsonObject }.getOrNull()
        ?: return "Réponse biométrique invalide."
    val options = beginObj["options"] ?: run {
        val msg = beginObj["error"]?.jsonPrimitive?.contentOrNull
            ?: beginObj["details"]?.jsonPrimitive?.contentOrNull
            ?: "Options passkey manquantes."
        return msg
    }
    val optionsJson = jsonFormat.encodeToString(JsonElement.serializer(), options)

    val credential = runCatching {
        browserCreatePasskeyCredential(optionsJson).unsafeCast<Promise<dynamic>>().await()
    }.getOrNull() ?: return "Création passkey annulée ou refusée."

    val finishBody = js("{}")
    finishBody.action = "finish_register"
    finishBody.credential = credential
    val headers = js("{}")
    headers["Content-Type"] = "application/json"
    headers["apikey"] = anon
    headers["Authorization"] = "Bearer ${session.accessToken}"
    val init = js("{}")
    init.method = "POST"
    init.headers = headers
    init.body = JSON.stringify(finishBody)
    val finishResponse = window.fetch("$base/functions/v1/webauthn-passkey", init).await()
    if (!finishResponse.ok) {
        val details = runCatching { finishResponse.text().await().lowercase() }.getOrNull().orEmpty()
        return when {
            "expectedorigin" in details || "expected origin" in details || "origin" in details || "rpid" in details ->
                "Enregistrement biométrique refusé sur cette URL. Ouvre l'app via https://histoire-du-soir-web-app.pages.dev puis réessaie."
            "challenge" in details -> "Session biométrique expirée. Réessaie."
            "already" in details || "exists" in details || "duplicate" in details ->
                "Une biométrie est déjà activée sur cet appareil."
            "registration verification failed" in details ->
                "Validation biométrique refusée. Réessaie depuis l'URL principale."
            else -> "Enregistrement biométrique refusé. Détail: ${details.take(180)}"
        }
    }

    localStorage.setItem(PASSKEY_ENABLED_HINT_KEY, "1")
    decodeJwtEmail(session.accessToken)?.trim()?.takeIf { it.contains('@') }?.let {
        localStorage.setItem(LAST_LOGIN_EMAIL_KEY, it)
    }
    return null
}

suspend fun resetPasskeysForCurrentUser(client: HttpClient): String? {
    if (!isSupabaseWebConfigured()) return "Supabase n’est pas configuré sur cette page."
    val session = ensureSupabaseSession(client) ?: return "Connecte-toi d'abord avec e-mail et mot de passe."
    val base = supabaseWebUrl() ?: return "Configuration Supabase incomplète."
    val anon = supabaseWebAnonKey() ?: return "Configuration Supabase incomplète."

    val payload = buildJsonObject { put("action", "reset_credentials") }
    return try {
        val response = client.post("$base/functions/v1/webauthn-passkey") {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer ${session.accessToken}")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), payload))
        }
        val text = runCatching { response.body<String>() }.getOrNull().orEmpty()
        if (response.status.value !in 200..299) {
            val msg = runCatching {
                val obj = jsonFormat.parseToJsonElement(text).jsonObject
                obj["error"]?.jsonPrimitive?.contentOrNull
                    ?: obj["details"]?.jsonPrimitive?.contentOrNull
                    ?: text.take(200)
            }.getOrNull().orEmpty()
            "Réinitialisation biométrique impossible: ${msg.ifBlank { "réponse serveur invalide" }}"
        } else {
            null
        }
    } catch (_: Throwable) {
        "Réinitialisation biométrique impossible pour le moment."
    }
}

suspend fun hasPasskeyForCurrentUser(client: HttpClient): Boolean {
    if (!isSupabaseWebConfigured()) return false
    val session = ensureSupabaseSession(client) ?: return false
    val base = supabaseWebUrl() ?: return false
    val anon = supabaseWebAnonKey() ?: return false
    val payload = buildJsonObject { put("action", "has_credentials") }
    return try {
        val response = client.post("$base/functions/v1/webauthn-passkey") {
            contentType(ContentType.Application.Json)
            header("apikey", anon)
            header("Authorization", "Bearer ${session.accessToken}")
            setBody(jsonFormat.encodeToString(JsonObject.serializer(), payload))
        }
        if (response.status.value !in 200..299) return false
        val text = runCatching { response.body<String>() }.getOrNull().orEmpty()
        val obj = runCatching { jsonFormat.parseToJsonElement(text).jsonObject }.getOrNull() ?: return false
        obj["has_credentials"]?.jsonPrimitive?.contentOrNull == "true"
    } catch (_: Throwable) {
        false
    }
}

private fun browserCreatePasskeyCredential(optionsJson: String): dynamic =
    js(
        """(function(optionsJson){
      function toBuf(v){
        var b64 = v.replace(/-/g, '+').replace(/_/g, '/');
        var pad = '='.repeat((4 - (b64.length % 4)) % 4);
        var bin = atob(b64 + pad);
        var out = new Uint8Array(bin.length);
        for (var i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
        return out;
      }
      function toB64url(buf){
        var bytes = new Uint8Array(buf);
        var s = '';
        for (var i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
        return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
      }
      var options = JSON.parse(optionsJson);
      var publicKey = Object.assign({}, options);
      publicKey.challenge = toBuf(options.challenge);
      if (publicKey.user && publicKey.user.id) {
        publicKey.user = Object.assign({}, publicKey.user, { id: toBuf(publicKey.user.id) });
      }
      if (Array.isArray(publicKey.excludeCredentials)) {
        publicKey.excludeCredentials = publicKey.excludeCredentials.map(function(c){
          return { type: c.type, id: toBuf(c.id), transports: c.transports };
        });
      }
      return navigator.credentials.create({ publicKey: publicKey }).then(function(cred){
        if (!cred) return null;
        var r = cred.response;
        return {
          id: cred.id,
          type: cred.type,
          rawId: toB64url(cred.rawId),
          response: {
            clientDataJSON: toB64url(r.clientDataJSON),
            attestationObject: toB64url(r.attestationObject),
            transports: (typeof r.getTransports === 'function') ? r.getTransports() : []
          }
        };
      }).catch(function(){ return null; });
    })""",
    )(optionsJson)
